import { useState } from 'react'
import { useMutation } from '@apollo/client/react'
import * as Sentry from '@sentry/react'
import { toast } from 'sonner'
import {
  CreatePresignedUrlDocument,
  type CreatePresignedUrlMutation,
  type CreatePresignedUrlMutationVariables,
  type DocumentType,
} from '@/graphql/generated/graphql'

interface UseUploadFileOptions {
  /** Tipo de documento. Default `IMAGE`. */
  documentType?: DocumentType
  /** `businessId` opcional para asociar la carga a un negocio. */
  businessId?: string
  /** `businessSlug` opcional. */
  businessSlug?: string
}

interface UseUploadFileResult {
  /**
   * Sube el archivo y retorna la URL pública persistente (la presigned URL sin
   * sus query params firmados), o `null` si falla. Esa URL es lo que se manda
   * al backend como `logo` / `businessLogo`.
   */
  upload: (file: File) => Promise<string | null>
  uploading: boolean
  error: Error | null
}

/**
 * El backend devuelve una presigned URL con query params firmados que expiran.
 * Lo que persistimos es la URL "pelada" (sin query string): ese path queda
 * accesible públicamente vía el CDN una vez completada la subida.
 */
function stripQuery(signedUrl: string): string {
  const i = signedUrl.indexOf('?')
  return i === -1 ? signedUrl : signedUrl.slice(0, i)
}

function extOf(file: File): string {
  const fromName = file.name.split('.').pop()?.toLowerCase()
  if (fromName) return fromName
  // Fallback al MIME type (`image/png` → `png`).
  const fromMime = file.type.split('/').pop()?.toLowerCase()
  return fromMime ?? ''
}

/**
 * Encapsula el flujo de carga vía presigned URL:
 *   1. Solicita URL firmada al gateway con metadata del archivo.
 *   2. Sube el archivo al storage con un `PUT` directo (sin Authorization —
 *      la URL incluye la firma).
 *   3. Retorna la `key` que el backend usará como referencia persistente.
 *
 * Errores se reportan a Sentry con `tags.operation = 'UploadFile'` y se
 * muestran como toast. El consumer recibe `null` para no acoplar UI a errores.
 */
export function useUploadFile({
  documentType = 'IMAGE',
  businessId,
  businessSlug,
}: UseUploadFileOptions = {}): UseUploadFileResult {
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const [createPresignedUrl] = useMutation<
    CreatePresignedUrlMutation,
    CreatePresignedUrlMutationVariables
  >(CreatePresignedUrlDocument)

  const upload = async (file: File): Promise<string | null> => {
    setUploading(true)
    setError(null)
    try {
      const result = await createPresignedUrl({
        variables: {
          input: {
            name: file.name,
            ext: extOf(file),
            size: file.size,
            documentType,
            businessId,
            businessSlug,
          },
        },
      })

      const signed = result.data?.createPresignedUrl
      if (!signed?.url) {
        throw new Error('El servidor no devolvió una URL de carga.')
      }

      const res = await fetch(signed.url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      })
      if (!res.ok) {
        throw new Error(`Subida fallida (HTTP ${res.status})`)
      }

      return stripQuery(signed.url)
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err))
      setError(e)
      Sentry.captureException(e, { tags: { operation: 'UploadFile' } })
      toast.error('No se pudo subir el archivo', { description: e.message })
      return null
    } finally {
      setUploading(false)
    }
  }

  return { upload, uploading, error }
}
